import React, { useContext, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { SnackbarContext } from "../../../../../context/snackbar-context";
import GenericDialog from "../../../../shared/generic-dialog";

interface ScriptStartSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  backgroundJobId: string;
  scriptName: string;
}

const ScriptStartSuccessDialog: React.FC<ScriptStartSuccessDialogProps> = ({
  open,
  onClose,
  backgroundJobId,
  scriptName,
}) => {
  const theme = useTheme();
  const { copyToClipboard } = useContext(SnackbarContext);
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = () => {
    copyToClipboard(backgroundJobId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <GenericDialog
      title="Script Started Successfully"
      open={open}
      handleDialogClose={onClose}
      hasAckBtn={true}
      acknowledgeBtnLabel="Close"
      width="md"
    >
      <Grid container spacing={3} paddingTop={2}>
        {/* Success Message */}
        <Grid item xs={12}>
          <Typography variant="h6" align="center" color="success.main">
            ✓ {scriptName} has been started
          </Typography>
        </Grid>

        {/* Background Job ID */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 2,
              backgroundColor: theme.palette.grey[100],
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Box sx={{ flex: 1, marginRight: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Background Job ID:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                }}
                data-testid="background-job-id"
              >
                {backgroundJobId}
              </Typography>
            </Box>
            <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
              <IconButton
                onClick={handleCopyToClipboard}
                color={copied ? "success" : "primary"}
                data-testid="copy-button"
                size="small"
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* Grafana Instructions */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              padding: 2,
              backgroundColor: theme.palette.info.light,
              borderRadius: 1,
              border: `1px solid ${theme.palette.info.main}`,
            }}
          >
            <InfoIcon color="info" />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <strong>How to view execution results:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                <ol style={{ margin: 0, paddingLeft: "20px" }}>
                  <li>Go to Grafana Loki logs viewer</li>
                  <li>Search for app: hippo-hangfire</li>
                  <li>Filter by the provided background job ID</li>
                </ol>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </GenericDialog>
  );
};

export default ScriptStartSuccessDialog;
